import { useState } from 'react'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'

import { HeldValidationSubmission } from '@percent/admin-dashboard/api/actions/validationSubmissions/validationSubmissions.types'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { AcknowledgeModal, ConfirmationModal, Modal } from '@percent/lemonade'
import { useMutation } from '@percent/utility'

type ReleaseHoldModalProps = {
  submission: HeldValidationSubmission | null
  onClose: VoidFunction
  onReleased: VoidFunction
}

const resultCopy = {
  released: {
    title: 'dialog.releaseHold.released.title',
    description: 'dialog.releaseHold.released.description'
  },
  alreadyReleased: {
    title: 'dialog.releaseHold.alreadyReleased.title',
    description: 'dialog.releaseHold.alreadyReleased.description'
  },
  error: {
    title: 'dialog.releaseHold.error.title',
    description: 'dialog.releaseHold.error.description'
  }
} as const

type Result = keyof typeof resultCopy

export function ReleaseHoldModal({ submission, onClose, onReleased }: Readonly<ReleaseHoldModalProps>) {
  const { t } = useTranslation()
  const { validationSubmissionsService } = useServices()
  const [result, setResult] = useState<Result | null>(null)

  const [{ isPending }, { apiFunc: releaseHold }] = useMutation(
    validationSubmissionsService.releaseValidationSubmissionHold,
    () => setResult('released'),
    err => setResult((err as AxiosError).response?.status === 409 ? 'alreadyReleased' : 'error')
  )

  const handleResultClose = () => {
    if (result !== 'error') {
      onReleased()
    }
    setResult(null)
    onClose()
  }

  if (result) {
    return (
      <Modal open onClose={handleResultClose} aria-labelledby="release-hold-result-modal">
        <AcknowledgeModal
          result={result === 'error' ? 'negative' : 'positive'}
          title={t(resultCopy[result].title)}
          description={t(resultCopy[result].description)}
          buttonText={t('button.close')}
          handleClose={handleResultClose}
        />
      </Modal>
    )
  }

  return (
    <Modal open={!!submission} onClose={onClose} aria-labelledby="release-hold-modal">
      <ConfirmationModal
        title={t('dialog.releaseHold.title')}
        primaryButtonText={t('button.releaseHold')}
        secondaryButtonText={t('button.cancel')}
        handleClose={onClose}
        handleSubmit={() => submission && releaseHold({ id: submission.id })}
        loading={isPending}
      >
        {t('dialog.releaseHold.description', { email: submission?.submission.email ?? submission?.id })}
      </ConfirmationModal>
    </Modal>
  )
}
